import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { StatusPill } from "@/components/shared/Visual";
import {
  ShieldCheck, MapPin, Clock, ClipboardCheck, ChevronRight,
  FileText, MessageSquare, LogOut, AlertTriangle, Users,
  Briefcase, Calendar as CalendarIcon, User,
} from "lucide-react";

/**
 * SECURITY GUARD "MY JOB" — mobile-first
 * Numbered sections matching the V2 mockup:
 *   1) Job Details  · 2) Your Team  · 3) Team Chat  · 4) Emergency
 * Plus shift timer + End Shift when checked in.
 */
export default function GuardMyJob() {
  const [job, setJob] = useState(null);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("job");

  const load = () => {
    setLoading(true);
    api.get("/ops/my-active-job")
      .then(async (r) => {
        const j = r.data?.job;
        setJob(j);
        if (j?.ops_room) {
          try {
            const att = await api.get(`/bookings/${j.id}/ops-room/attendance`);
            setTeam(att.data?.roster || []);
          } catch { setTeam([]); }
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
  }, []);

  const timer = useShiftTimer(job?.arrived_at);

  const checkIn = async () => {
    if (!job) return;
    setBusy(true);
    try {
      await api.post(`/bookings/${job.id}/ops-room/attendance/self-checkin`);
      toast.success("Checked in");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const emergency = () => {
    toast.error("Emergency alert broadcast to team lead & company (mock).", { duration: 6000 });
  };

  if (loading) return <div className="p-8 text-center txt-lo font-manrope">Loading…</div>;

  if (!job) {
    return (
      <div className="max-w-md mx-auto space-y-6" data-testid="guard-myjob-empty">
        <div className="page-hero text-center relative">
          <ShieldCheck className="w-10 h-10 text-brand mx-auto mb-3 relative z-10" />
          <div className="eyebrow eyebrow-brand mb-2 relative z-10">My Job</div>
          <h1 className="text-2xl font-outfit font-bold txt-hi relative z-10">No active shift</h1>
          <p className="txt-md text-sm font-manrope mt-2 relative z-10">You have no deployment scheduled for today.</p>
          <Link to="/staff/jobs" className="btn-brand mt-4 inline-flex relative z-10">Browse jobs</Link>
        </div>
      </div>
    );
  }

  const cl = job.checklist || {};
  const dt = job.date ? new Date(job.date) : null;
  const dd = dt ? String(dt.getDate()).padStart(2, "0") : "--";
  const mm = dt ? dt.toLocaleString("en-GB", { month: "short" }).toUpperCase() : "";
  const dw = dt ? dt.toLocaleString("en-GB", { weekday: "short" }).toUpperCase() : "";

  return (
    <div className="max-w-md mx-auto space-y-4 pb-24" data-testid="guard-myjob-page">
      {/* Header hero */}
      <div className="card-dark p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex gap-3">
            <div className="w-16 rounded-lg card-inner text-center py-2 flex-shrink-0">
              <div className="eyebrow role-manager-accent">{mm}</div>
              <div className="text-3xl font-outfit font-bold txt-hi leading-none mt-1">{dd}</div>
              <div className="eyebrow mt-1">{dw}</div>
            </div>
            <div className="min-w-0">
              <div className="font-outfit font-bold txt-hi text-lg leading-tight">{job.title}</div>
              <div className="text-xs txt-lo font-manrope mt-1 flex items-center gap-1">
                <MapPin className="w-3 h-3" /> {job.location}
              </div>
              <div className="text-xs txt-lo font-manrope mt-0.5 flex items-center gap-1">
                <Clock className="w-3 h-3" /> {job.start_time || "—"} – {job.end_time || "—"}
              </div>
              <div className="text-xs txt-md font-manrope mt-1">Role: <span className="text-violet-300 font-semibold">Security Guard</span></div>
            </div>
          </div>
          <div className="text-right">
            <StatusPill kind={job.checked_in ? "active" : "standby"}>{job.checked_in ? "On shift" : "Confirmed"}</StatusPill>
            {!job.checked_in && job.start_time && (
              <div className="mt-2">
                <div className="eyebrow">Check-in</div>
                <div className="font-outfit font-bold txt-hi text-lg leading-none">{job.start_time}</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Shift status card */}
      {job.checked_in ? (
        <div className="card-dark p-5 text-center" data-testid="guard-shift-timer">
          <div className="eyebrow role-guard-accent mb-2">On-site time</div>
          <div className="shift-timer">{timer}</div>
          <button className="btn-endshift mt-4 flex items-center justify-center gap-2" data-testid="guard-end-shift">
            <LogOut className="w-4 h-4" /> End shift
          </button>
        </div>
      ) : (
        <button onClick={checkIn} disabled={busy} className="btn-endshift w-full" data-testid="guard-check-in-btn">
          {busy ? "Checking in…" : "Check in now"}
        </button>
      )}

      {/* Section 1 — Job Details */}
      <Section num={1} label="Job Details">
        <SectionRow icon={FileText} title="Site Plan & Instructions" sub="SOPs, emergency procedures, site rules"
          to={job.ops_room ? `/ops-room/${job.id}` : `/staff/bookings`} testId="guard-sec-site-plan" />
        <SectionRow icon={MapPin} title="Location" sub={job.location} />
        <SectionRow icon={User} title="Notes from Company" sub="Please read all instructions before arrival" />
      </Section>

      {/* Section 2 — Your Team */}
      {team.length > 0 && (
        <Section num={2} label={`Your Team (${team.length})`} rightLink={job.ops_room ? { to: `/ops-room/${job.id}`, label: "View all" } : null}>
          <div className="flex gap-3 overflow-x-auto hide-scroll -mx-1 px-1">
            {team.map((m) => (
              <div key={m.user_id} className="flex-shrink-0 text-center w-16">
                <div className="relative mx-auto w-12 h-12 rounded-full bg-navy-800 border border-white/10 flex items-center justify-center">
                  <User className="w-5 h-5 txt-lo" />
                  {m.arrived_at && <span className="absolute bottom-0 right-0 w-3 h-3 rounded-full bg-gold-400 border-2 border-navy-900" />}
                </div>
                <div className="text-xs txt-hi font-manrope mt-1 truncate">{(m.name || "").split(" ").slice(0, 2).map((n, i) => i === 0 ? n : n[0] + ".").join(" ")}</div>
                {m.is_me && <div className="eyebrow role-manager-accent">You</div>}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Section 3 — Team Chat */}
      {job.ops_room && (
        <Section num={3} label="Team Chat">
          <Link to={`/ops-room/${job.id}`} className="flex items-center gap-3 p-2 hover:bg-white/5 rounded-md" data-testid="guard-team-chat">
            <div className="w-10 h-10 rounded-md bg-violet-500/15 text-violet-400 flex items-center justify-center">
              <MessageSquare className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-outfit font-semibold txt-hi text-sm">Team Chat</div>
              <div className="eyebrow mt-0.5">Communicate with your team</div>
            </div>
            <ChevronRight className="w-4 h-4 txt-lo" />
          </Link>
        </Section>
      )}

      {/* Section 4 — Emergency */}
      <Section num={4} label="Emergency" tone="rose">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-rose-400 flex-shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1">
            <div className="font-outfit font-semibold text-rose-200 text-sm">In an emergency?</div>
            <div className="text-xs text-rose-200/70 font-manrope mt-0.5">Tap to alert your team lead and the security company.</div>
          </div>
        </div>
        <button onClick={emergency}
          className="w-full mt-3 py-3.5 rounded-lg bg-rose-500 hover:bg-rose-600 text-white font-outfit font-bold flex items-center justify-center gap-2"
          data-testid="guard-emergency-btn"
        >
          <AlertTriangle className="w-4 h-4" /> EMERGENCY BUTTON
        </button>
      </Section>

      {/* Checklist */}
      <div className="card-dark p-4">
        <div className="eyebrow eyebrow-brand mb-3 flex items-center gap-2">
          <ClipboardCheck className="w-3.5 h-3.5" /> My Checklist
        </div>
        <div className="space-y-2">
          <CheckRow label="Check-in" done={cl.check_in} time={job.arrived_at} />
          <CheckRow label="SOP Acknowledged" done={cl.sop_ack} />
          <CheckRow label="Briefing Completed" done={cl.briefing} />
          <CheckRow label="Equipment Check" done={cl.equipment} />
          <CheckRow label="Post Check-out Report" done={cl.report_ready} />
        </div>
      </div>

      {/* Mobile bottom tabs */}
      <div className="mtabs fixed bottom-0 left-0 right-0 z-30 lg:hidden">
        <button onClick={() => setTab("job")} className={`mtab mtab-guard ${tab === "job" ? "active" : ""}`}><Briefcase className="w-4 h-4 mb-1" /> My Job</button>
        <Link to="/staff/bookings" className="mtab"><CalendarIcon className="w-4 h-4 mb-1" /> Shifts</Link>
        <Link to={job.ops_room ? `/ops-room/${job.id}` : "/staff/negotiations"} className="mtab"><MessageSquare className="w-4 h-4 mb-1" /> Messages</Link>
        <Link to="/staff/profile" className="mtab"><User className="w-4 h-4 mb-1" /> Profile</Link>
      </div>
    </div>
  );
}

function Section({ num, label, tone = "manager", rightLink, children }) {
  const dotTone = { manager: "role-band-manager", guard: "role-band-guard", owner: "role-band-owner", rose: "" }[tone];
  const rose = tone === "rose";
  return (
    <div className={`${rose ? "alert-card !p-4 flex-col items-stretch" : "card-dark p-4"}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className={`w-6 h-6 rounded-full text-[11px] font-outfit font-bold flex items-center justify-center ${rose ? "bg-rose-500/30 text-rose-200" : "bg-violet-500/20 text-violet-300 border border-violet-500/30"}`}>{num}</span>
          <div className={`eyebrow ${rose ? "text-rose-300" : "eyebrow-brand"}`}>{label}</div>
        </div>
        {rightLink && <Link to={rightLink.to} className="text-xs font-manrope font-semibold text-violet-300 hover:text-violet-100">{rightLink.label}</Link>}
      </div>
      {children}
    </div>
  );
}

function SectionRow({ icon: Icon, title, sub, to, testId }) {
  const inner = (
    <div className="flex items-center gap-3 py-2 hover:bg-white/5 rounded-md px-1 -mx-1 transition-colors" data-testid={testId}>
      <div className="w-9 h-9 rounded-md bg-violet-500/15 text-violet-400 flex items-center justify-center flex-shrink-0">
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-outfit font-semibold txt-hi text-sm">{title}</div>
        {sub && <div className="text-xs txt-lo font-manrope truncate">{sub}</div>}
      </div>
      <ChevronRight className="w-4 h-4 txt-lo" />
    </div>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
}

function CheckRow({ label, done, time }) {
  return (
    <div className={`check-item ${done ? "done" : ""}`}>
      <span className={`check-dot ${done ? "done" : ""}`} />
      <div className="flex-1">
        <div className={`text-sm font-manrope ${done ? "txt-hi" : "txt-md"}`}>{label}</div>
      </div>
      {done && time && <div className="eyebrow role-guard-accent">{new Date(time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>}
    </div>
  );
}

function useShiftTimer(arrivedAt) {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((v) => v + 1), 1000);
    return () => clearInterval(t);
  }, []);
  return useMemo(() => {
    if (!arrivedAt) return "00:00:00";
    const start = new Date(arrivedAt).getTime();
    const diff = Math.max(0, Math.floor((Date.now() - start) / 1000));
    const h = String(Math.floor(diff / 3600)).padStart(2, "0");
    const m = String(Math.floor((diff % 3600) / 60)).padStart(2, "0");
    const s = String(diff % 60).padStart(2, "0");
    return `${h}:${m}:${s}`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arrivedAt, tick]);
}
